import Service from 'react-reservice';

import announcementsAPI, { IAnnouncement } from '@webtrader/core/api/config/announcement';
import assetAPI, { EAssetType, IAsset } from '@webtrader/core/api/config/asset';
import dayjs from '@webtrader/core/dayjs';

export enum EAppType {
  MOBILE = 'MOBILE',
  PC = 'PC'
}

interface IConfigContext {
  announcements?: IAnnouncement[];
  assets: IAsset[];
  loading: boolean;
  splashAsset?: IAsset;
  appType: EAppType;
  symbolTopicDebounce: number;
}

const defaultContext: IConfigContext = {
  announcements: [],
  assets: [],
  loading: true,
  splashAsset: undefined,
  appType: EAppType.PC,
  symbolTopicDebounce: 50
};

class ConfigService extends Service<IConfigContext> {
  constructor() {
    super();

    this.context = {
      ...defaultContext
    };
  }

  public init = (isNative?: boolean) => {
    this.retrieveAssets(isNative);
    this.retrieveAnnouncements();
  };

  public initAnnouncements = () => {
    this.retrieveAnnouncements();
  };

  private retrieveAnnouncements = async () => {
    try {
      this.context.loading = true;

      const { result } = await announcementsAPI();

      if (result && result.length > 0) {
        this.context.announcements = result;
      } else {
        this.context.announcements = undefined;
      }
    } finally {
      this.context.loading = false;
    }
  };

  private retrieveAssets = async (isNative?: boolean) => {
    try {
      this.context.loading = true;

      const { result: carouselAssetsAPIResult } = await assetAPI({ assetType: EAssetType.CAROUSEL });
      this.context.assets = carouselAssetsAPIResult || [];

      if (isNative) {
        const { result: splashAssetAPIResult = [] } = await assetAPI({ assetType: EAssetType.SPLASH });

        if (splashAssetAPIResult && splashAssetAPIResult.length > 0) {
          const splashAssetsPublishedBeforeNow = splashAssetAPIResult.filter((splashAsset) => {
            return dayjs(splashAsset.publishDate).isBefore(dayjs());
          });

          const sortedSplashAssets = splashAssetsPublishedBeforeNow.sort((a, b) => {
            return b.publishDate - a.publishDate;
          });

          this.context.splashAsset = sortedSplashAssets[0];
        } else {
          this.context.splashAsset = undefined;
        }
      }
    } finally {
      this.context.loading = false;
    }
  };

  public setAppType = (appType: EAppType) => {
    this.context.appType = appType;
    if (appType === EAppType.PC) {
      this.context.symbolTopicDebounce = 50;
    } else {
      this.context.symbolTopicDebounce = 400;
    }
  };

  public getAppType = () => this.context.appType;
}

export default new ConfigService();
